import { useState, useEffect } from "react";
import axios from "axios";
import PriceInput from "../components/PriceInput";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import {
  DollarSign,
  Users,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  RefreshCw,
  Search,
  Download,
  XCircle,
  CheckCircle,
  Clock,
  CreditCard,
  Banknote,
  PieChart,
  BarChart3,
  Calendar,
  Phone,
  Mail,
  Store,
  Plus,
  X,
  Check,
  MessageCircle,
} from "lucide-react";
import {
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from 'recharts';
import toast from "react-hot-toast";

const API = `${window.location.origin}/api`;

export default function InstallmentReportsPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [paymentForm, setPaymentForm] = useState({
    amount: 0,
    payment_method: "cash",
    notes: "",
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/license/admin/installment-reports`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(response.data);
    } catch (error) {
      toast.error("حدث خطأ في جلب البيانات");
    } finally {
      setLoading(false);
    }
  };

  const recordPayment = async () => {
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${API}/license/admin/record-payment?user_email=${selectedUser.user.email}&amount=${paymentForm.amount}&payment_method=${paymentForm.payment_method}&notes=${paymentForm.notes}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("تم تسجيل الدفعة بنجاح");
      setShowPaymentModal(false);
      setPaymentForm({ amount: 0, payment_method: "cash", notes: "" });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "حدث خطأ");
    }
  };

  const cancelLicense = async () => {
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${API}/license/admin/cancel-license/${selectedUser.user.email}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("تم إلغاء الترخيص");
      setShowCancelModal(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "حدث خطأ");
    }
  };

  const openWhatsApp = (phone, name, pending) => {
    const message = encodeURIComponent(
      `مرحباً ${name}،\nنذكرك بأن لديك مبلغ متبقي قدره $${pending} من قسط ترخيص تطبيق نظاريو حساباتي.\nنرجو التواصل معنا لترتيب الدفع.`
    );
    window.open(`https://wa.me/${phone}?text=${message}`, '_blank');
  };

  const exportToCSV = () => {
    if (!data?.users) return;
    
    const headers = ["الاسم", "البريد", "الهاتف", "المتجر", "النوع", "الإجمالي", "المدفوع", "المتبقي", "الحالة"];
    const rows = data.users.map(u => [
      u.user?.name || "",
      u.user?.email || "",
      u.user?.phone || "",
      u.user?.store_name || "",
      u.license_type,
      u.total_amount,
      u.paid_amount,
      u.pending_amount,
      u.status
    ]);
    
    const csvContent = [headers, ...rows].map(r => r.join(",")).join("\n");
    const blob = new Blob(["\uFEFF" + csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `installment_report_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    toast.success("تم تصدير التقرير");
  };

  const filteredUsers = data?.users?.filter(u =>
    u.user?.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    u.user?.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    u.user?.store_name?.toLowerCase().includes(searchQuery.toLowerCase())
  ) || [];

  const pieData = data ? [
    { name: 'المدفوع', value: data.summary.total_received, color: '#10b981' },
    { name: 'المتبقي', value: data.summary.total_pending, color: '#ef4444' },
  ].filter(d => d.value > 0) : [];

  return (
    <div className="p-4 md:p-6 space-y-6" data-testid="installment-reports-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-lg shadow-emerald-500/30">
            <DollarSign className="w-7 h-7 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white">تقارير الأقساط المالية</h1>
            <p className="text-slate-400 text-sm">متابعة المدفوعات والمتأخرات</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={fetchData} disabled={loading} className="bg-slate-800 hover:bg-slate-700">
            <RefreshCw className={`w-4 h-4 me-2 ${loading ? 'animate-spin' : ''}`} />
            تحديث
          </Button>
          <Button onClick={exportToCSV} className="bg-emerald-500 hover:bg-emerald-600">
            <Download className="w-4 h-4 me-2" />
            تصدير
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12">
          <RefreshCw className="w-8 h-8 animate-spin text-emerald-500 mx-auto mb-4" />
          <p className="text-slate-400">جاري التحميل...</p>
        </div>
      ) : data && (
        <>
          {/* إحصائيات */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <Card className="bg-gradient-to-br from-blue-500/10 to-blue-600/10 border-blue-500/30">
              <CardContent className="p-4 text-center">
                <Users className="w-6 h-6 text-blue-500 mx-auto mb-2" />
                <p className="text-2xl font-bold text-blue-400">{data.summary.total_installment_users}</p>
                <p className="text-xs text-slate-400">عملاء الأقساط</p>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-purple-500/10 to-purple-600/10 border-purple-500/30">
              <CardContent className="p-4 text-center">
                <TrendingUp className="w-6 h-6 text-purple-500 mx-auto mb-2" />
                <p className="text-2xl font-bold text-purple-400">${data.summary.total_expected}</p>
                <p className="text-xs text-slate-400">المتوقع</p>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-emerald-500/10 to-emerald-600/10 border-emerald-500/30">
              <CardContent className="p-4 text-center">
                <CheckCircle className="w-6 h-6 text-emerald-500 mx-auto mb-2" />
                <p className="text-2xl font-bold text-emerald-400">${data.summary.total_received}</p>
                <p className="text-xs text-slate-400">المحصّل</p>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-red-500/10 to-red-600/10 border-red-500/30">
              <CardContent className="p-4 text-center">
                <Clock className="w-6 h-6 text-red-500 mx-auto mb-2" />
                <p className="text-2xl font-bold text-red-400">${data.summary.total_pending}</p>
                <p className="text-xs text-slate-400">المتبقي</p>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-amber-500/10 to-amber-600/10 border-amber-500/30">
              <CardContent className="p-4 text-center">
                <PieChart className="w-6 h-6 text-amber-500 mx-auto mb-2" />
                <p className="text-2xl font-bold text-amber-400">{data.summary.collection_rate}%</p>
                <p className="text-xs text-slate-400">نسبة التحصيل</p>
              </CardContent>
            </Card>
          </div>

          {/* الرسوم البيانية */}
          <div className="grid md:grid-cols-2 gap-6">
            {/* Pie Chart */}
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <PieChart className="w-5 h-5 text-emerald-500" />
                  توزيع المدفوعات
                </CardTitle>
              </CardHeader>
              <CardContent>
                {pieData.length > 0 ? (
                  <div className="flex items-center justify-center gap-6">
                    <div className="h-48 w-48">
                      <ResponsiveContainer width="100%" height="100%">
                        <RechartsPieChart>
                          <Pie
                            data={pieData}
                            cx="50%"
                            cy="50%"
                            innerRadius={50}
                            outerRadius={80}
                            paddingAngle={3}
                            dataKey="value"
                          >
                            {pieData.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry.color} />
                            ))}
                          </Pie>
                          <Tooltip formatter={(value) => [`$${value}`, '']} />
                        </RechartsPieChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="space-y-3">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-emerald-500" />
                        <span className="text-slate-400">المدفوع:</span>
                        <span className="text-emerald-400 font-bold">${data.summary.total_received}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-red-500" />
                        <span className="text-slate-400">المتبقي:</span>
                        <span className="text-red-400 font-bold">${data.summary.total_pending}</span>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8 text-slate-400">لا توجد بيانات</div>
                )}
              </CardContent>
            </Card>

            {/* المتأخرين */}
            <Card className="bg-gradient-to-br from-red-500/5 to-red-600/5 border-red-500/30">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-red-500" />
                  العملاء المتأخرين ({data.summary.overdue_count})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {data.overdue_users?.length > 0 ? (
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {data.overdue_users.slice(0, 5).map((user, index) => (
                      <div key={index} className="flex items-center justify-between p-2 rounded-lg bg-slate-900/50">
                        <div>
                          <p className="text-white text-sm">{user.user?.name || user.user?.email}</p>
                          <p className="text-xs text-slate-400">{user.user?.store_name}</p>
                        </div>
                        <span className="text-red-400 font-bold">${user.pending_amount}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-emerald-400">
                    <CheckCircle className="w-12 h-12 mx-auto mb-2" />
                    لا يوجد متأخرين!
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* بحث */}
          <div className="relative">
            <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <Input
              placeholder="بحث عن عميل..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="bg-slate-800/50 border-slate-700 text-white pr-10"
            />
          </div>

          {/* جدول العملاء */}
          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white">تفاصيل العملاء</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-900/50">
                    <tr>
                      <th className="text-right p-4 text-slate-400 font-medium">العميل</th>
                      <th className="text-right p-4 text-slate-400 font-medium">النوع</th>
                      <th className="text-right p-4 text-slate-400 font-medium">الأقساط</th>
                      <th className="text-right p-4 text-slate-400 font-medium">المدفوع</th>
                      <th className="text-right p-4 text-slate-400 font-medium">المتبقي</th>
                      <th className="text-right p-4 text-slate-400 font-medium">الحالة</th>
                      <th className="text-right p-4 text-slate-400 font-medium">إجراءات</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.map((user, index) => (
                      <tr key={index} className="border-t border-slate-700 hover:bg-slate-800/50">
                        <td className="p-4">
                          <div>
                            <p className="text-white font-medium">{user.user?.name || "بدون اسم"}</p>
                            <p className="text-slate-400 text-xs">{user.user?.email}</p>
                            {user.user?.phone && (
                              <p className="text-slate-500 text-xs">{user.user?.phone}</p>
                            )}
                          </div>
                        </td>
                        <td className="p-4">
                          <Badge className={user.license_type === "professional" ? "bg-amber-500" : "bg-blue-500"}>
                            {user.license_type === "professional" ? "احترافي" : "عادي"}
                          </Badge>
                        </td>
                        <td className="p-4 text-slate-300">
                          {user.installments_paid} / {user.total_installments}
                        </td>
                        <td className="p-4">
                          <span className="text-emerald-400 font-bold">${user.paid_amount}</span>
                        </td>
                        <td className="p-4">
                          <span className={`font-bold ${user.pending_amount > 0 ? 'text-red-400' : 'text-emerald-400'}`}>
                            ${user.pending_amount}
                          </span>
                        </td>
                        <td className="p-4">
                          {user.status === "active" ? (
                            <Badge className="bg-emerald-500">مكتمل</Badge>
                          ) : (
                            <Badge className="bg-amber-500">جزئي</Badge>
                          )}
                        </td>
                        <td className="p-4">
                          <div className="flex gap-1">
                            {user.pending_amount > 0 && (
                              <>
                                <Button
                                  size="sm"
                                  onClick={() => {
                                    setSelectedUser(user);
                                    setPaymentForm({ ...paymentForm, amount: user.pending_amount / (user.total_installments - user.installments_paid) });
                                    setShowPaymentModal(true);
                                  }}
                                  className="bg-emerald-500 hover:bg-emerald-600"
                                >
                                  <Plus className="w-4 h-4" />
                                </Button>
                                {user.user?.phone && (
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => openWhatsApp(user.user.phone, user.user.name, user.pending_amount)}
                                    className="text-green-400 hover:text-green-300"
                                  >
                                    <MessageCircle className="w-4 h-4" />
                                  </Button>
                                )}
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => {
                                    setSelectedUser(user);
                                    setShowCancelModal(true);
                                  }}
                                  className="text-red-400 hover:text-red-300"
                                >
                                  <XCircle className="w-4 h-4" />
                                </Button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* سجل الإلغاءات */}
          {data.cancellations?.length > 0 && (
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <XCircle className="w-5 h-5 text-red-500" />
                  سجل الإلغاءات
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {data.cancellations.map((c, index) => (
                    <div key={index} className="flex items-center justify-between p-3 rounded-lg bg-red-500/10 border border-red-500/20">
                      <div>
                        <p className="text-white">{c.user_email}</p>
                        <p className="text-xs text-slate-400">
                          {new Date(c.cancelled_at).toLocaleDateString('ar-IQ')} - بواسطة {c.cancelled_by}
                        </p>
                      </div>
                      <div className="text-left">
                        <p className="text-red-400 font-bold">{c.previous_license}</p>
                        <p className="text-xs text-slate-400">مدفوع: ${c.paid_amount}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* Modal تسجيل دفعة */}
      {showPaymentModal && selectedUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
          <Card className="w-full max-w-md bg-slate-800 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Plus className="w-5 h-5 text-emerald-500" />
                تسجيل دفعة
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-slate-900/50 rounded-lg p-3">
                <p className="text-white font-medium">{selectedUser.user?.name}</p>
                <p className="text-slate-400 text-sm">{selectedUser.user?.email}</p>
                <p className="text-xs text-slate-500 mt-1">
                  المتبقي: <span className="text-red-400">${selectedUser.pending_amount}</span>
                </p>
              </div>
              <div>
                <label className="text-sm text-slate-400 mb-1 block">المبلغ</label>
                <PriceInput
                  value={parseFloat(paymentForm.amount) || 0}
                  onChange={(v) => setPaymentForm({...paymentForm, amount: v})}
                  placeholder="0"
                  testId="installment-report-amount-input"
                  className="bg-slate-700 border-slate-600 text-white"
                />
              </div>
              <div>
                <label className="text-sm text-slate-400 mb-1 block">طريقة الدفع</label>
                <select
                  value={paymentForm.payment_method}
                  onChange={(e) => setPaymentForm({...paymentForm, payment_method: e.target.value})}
                  className="w-full bg-slate-700 border border-slate-600 text-white rounded-lg px-4 py-2"
                >
                  <option value="cash">نقدي</option>
                  <option value="bank">تحويل بنكي</option>
                  <option value="card">بطاقة</option>
                </select>
              </div>
              <div>
                <label className="text-sm text-slate-400 mb-1 block">ملاحظات</label>
                <Input
                  value={paymentForm.notes}
                  onChange={(e) => setPaymentForm({...paymentForm, notes: e.target.value})}
                  className="bg-slate-700 border-slate-600 text-white"
                  placeholder="اختياري..."
                />
              </div>
              <div className="flex gap-2 pt-4">
                <Button onClick={() => setShowPaymentModal(false)} variant="outline" className="flex-1 border-slate-600">
                  إلغاء
                </Button>
                <Button onClick={recordPayment} className="flex-1 bg-emerald-500 hover:bg-emerald-600">
                  <Check className="w-4 h-4 me-2" />
                  تسجيل
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Modal إلغاء الترخيص */}
      {showCancelModal && selectedUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
          <Card className="w-full max-w-md bg-slate-800 border-red-500/30">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-red-500" />
                تأكيد إلغاء الترخيص
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
                <p className="text-red-400 text-sm">
                  هل أنت متأكد من إلغاء ترخيص <strong>{selectedUser.user?.name}</strong>؟
                </p>
                <p className="text-slate-400 text-xs mt-2">
                  سيتم إرجاع المستخدم للنسخة التجريبية ولن يتمكن من استخدام الميزات المدفوعة.
                </p>
              </div>
              <div className="bg-slate-900/50 rounded-lg p-3">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-400">المدفوع:</span>
                  <span className="text-emerald-400">${selectedUser.paid_amount}</span>
                </div>
                <div className="flex justify-between text-sm mt-1">
                  <span className="text-slate-400">المتبقي:</span>
                  <span className="text-red-400">${selectedUser.pending_amount}</span>
                </div>
              </div>
              <div className="flex gap-2 pt-4">
                <Button onClick={() => setShowCancelModal(false)} variant="outline" className="flex-1 border-slate-600">
                  تراجع
                </Button>
                <Button onClick={cancelLicense} className="flex-1 bg-red-500 hover:bg-red-600">
                  <XCircle className="w-4 h-4 me-2" />
                  إلغاء الترخيص
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
